import { type ReactNode, useEffect, useRef } from 'react'
import { Box, Divider, IconButton, Stack, Tooltip } from '@mui/material'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import { useEditor, useEditorState, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { TableKit } from '@tiptap/extension-table'
import { Placeholder } from '@tiptap/extensions'
import type { TMessageDoc } from '@modules/types'
import { CARD_COLORS } from '@ds/theme/color-mode'
import { messageContentSx } from './messageContentSx'
import { TableControls } from './TableControls'

export type TMessageEditorProps = {
  value: TMessageDoc | null
  placeholder?: string
  disabled?: boolean
  expanded?: boolean
  actions?: ReactNode
  onChange: (doc: TMessageDoc | null) => void
  onEnter: () => void
}

type TFormatButton = {
  title: string
  mark: string
  icon: ReactNode
  toggle: (editor: Editor) => boolean
}

const FORMAT_BUTTONS: TFormatButton[] = [
  {
    title: 'Bold',
    mark: 'bold',
    icon: <FormatBoldIcon fontSize="small" />,
    toggle: editor => editor.chain().focus().toggleBold().run(),
  },
  {
    title: 'Italic',
    mark: 'italic',
    icon: <FormatItalicIcon fontSize="small" />,
    toggle: editor => editor.chain().focus().toggleItalic().run(),
  },
  {
    title: 'Bulleted list',
    mark: 'bulletList',
    icon: <FormatListBulletedIcon fontSize="small" />,
    toggle: editor => editor.chain().focus().toggleBulletList().run(),
  },
  {
    title: 'Numbered list',
    mark: 'orderedList',
    icon: <FormatListNumberedIcon fontSize="small" />,
    toggle: editor => editor.chain().focus().toggleOrderedList().run(),
  },
]

export const MessageEditor = ({
  value,
  placeholder = 'Type a message...',
  disabled = false,
  expanded = false,
  actions,
  onChange,
  onEnter,
}: TMessageEditorProps) => {
  const enterRef = useRef(onEnter)
  enterRef.current = onEnter

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        horizontalRule: false,
        blockquote: false,
        code: false,
        strike: false,
        underline: false,
        link: false,
      }),
      TableKit.configure({ table: { resizable: false } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value ?? '',
    editable: !disabled,
    editorProps: {
      handleKeyDown: (_view, event) => {
        if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) {
          return false
        }
        event.preventDefault()
        enterRef.current()
        return true
      },
    },
    onUpdate: ({ editor: instance }) => onChange(instance.isEmpty ? null : instance.getJSON()),
  })

  const active = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      marks: FORMAT_BUTTONS.filter(button => instance?.isActive(button.mark)).map(
        button => button.mark,
      ),
      isInTable: instance?.isActive('table') ?? false,
    }),
  })

  useEffect(() => {
    if (editor && value === null && !editor.isEmpty) {
      editor.commands.clearContent()
    }
  }, [editor, value])

  useEffect(() => {
    editor?.setEditable(!disabled)
  }, [editor, disabled])

  if (!editor) {
    return null
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        minWidth: 0,
        border: 1,
        borderColor: CARD_COLORS.closedBorder,
        borderRadius: 1,
        opacity: disabled ? 0.5 : 1,
        '&:focus-within': {
          borderColor: 'primary.main',
        },
      }}
    >
      <Box
        sx={{
          p: 1.75,
          maxHeight: expanded ? '60vh' : '40vh',
          overflowY: 'auto',
          '& .ProseMirror': {
            ...messageContentSx,
            fontSize: 15,
            outline: 'none',
            minHeight: expanded ? '30vh' : 45,
            '& p.is-editor-empty:first-of-type::before': {
              content: 'attr(data-placeholder)',
              color: 'text.disabled',
              float: 'left',
              height: 0,
              pointerEvents: 'none',
            },
            '& .tableWrapper': {
              overflowX: 'auto',
            },
            '& .selectedCell': {
              bgcolor: 'action.selected',
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ px: 1, py: 0.5, borderTop: 1, borderColor: CARD_COLORS.panelDivider }}
      >
        {FORMAT_BUTTONS.map(button => (
          <Tooltip key={button.mark} title={button.title}>
            <IconButton
              size="small"
              disabled={disabled}
              color={active?.marks.includes(button.mark) ? 'primary' : 'default'}
              onClick={() => button.toggle(editor)}
            >
              {button.icon}
            </IconButton>
          </Tooltip>
        ))}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

        <TableControls editor={editor} isInTable={active?.isInTable ?? false} disabled={disabled} />

        <Box flexGrow={1} />

        {actions}
      </Stack>
    </Box>
  )
}
